import React from "react";
import PropTypes from "prop-types";
import { ExternalLink, Paperclip } from "lucide-react";
import { getMessage } from "../data/locales";
import {
  listAnnouncementAttachments,
  listAnnouncementLinks,
} from "../utils/listAnnouncementLinks";
import "./AnnouncementInteractiveResources.css";

/**
 * @param {{
 *   href: string,
 *   kind: "link" | "file",
 *   title: string,
 *   subtitle?: string,
 *   cta: string,
 *   ariaLabel: string,
 * }} props
 */
function AnnouncementResourceAction({ href, kind, title, subtitle, cta, ariaLabel }) {
  const Icon = kind === "file" ? Paperclip : ExternalLink;
  return (
    <a
      className={`announcement-resource announcement-resource--${kind}`}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={ariaLabel}
    >
      <span className="announcement-resource__icon" aria-hidden="true">
        <Icon size={18} strokeWidth={2.25} />
      </span>
      <span className="announcement-resource__text">
        <span className="announcement-resource__title">{title}</span>
        {subtitle ? (
          <span className="announcement-resource__subtitle">{subtitle}</span>
        ) : null}
      </span>
      <span className="announcement-resource__cta">{cta}</span>
    </a>
  );
}

AnnouncementResourceAction.propTypes = {
  href: PropTypes.string.isRequired,
  kind: PropTypes.oneOf(["link", "file"]).isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  cta: PropTypes.string.isRequired,
  ariaLabel: PropTypes.string.isRequired,
};

AnnouncementResourceAction.defaultProps = {
  subtitle: "",
};

/**
 * @param {{
 *   announcementId: string,
 *   links?: Array<{ label?: string, url?: string }> | null,
 *   attachments?: Array<{ filename?: string, url?: string, mimeType?: string }> | null,
 * }} props
 */
function AnnouncementInteractiveResources({ announcementId, links, attachments }) {
  const visibleLinks = listAnnouncementLinks(links);
  const visibleFiles = listAnnouncementAttachments(attachments);
  if (visibleLinks.length === 0 && visibleFiles.length === 0) {
    return null;
  }

  return (
    <div className="announcement-resources">
      {visibleFiles.length > 0 ? (
        <section className="announcement-resources__group" aria-label={getMessage("announcements.attachmentsHeading")}>
          <h4 className="announcement-resources__heading">{getMessage("announcements.attachmentsHeading")}</h4>
          <ul className="announcement-resources__list">
            {visibleFiles.map((file, index) => {
              const title = file.filename || getMessage("announcements.fileFallback");
              return (
                <li key={`${announcementId}-file-${index}`}>
                  <AnnouncementResourceAction
                    kind="file"
                    href={file.url}
                    title={title}
                    subtitle={file.mimeType}
                    cta={getMessage("announcements.openFile")}
                    ariaLabel={getMessage("announcements.openFileAria", { name: title })}
                  />
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}
      {visibleLinks.length > 0 ? (
        <section className="announcement-resources__group" aria-label={getMessage("announcements.linksHeading")}>
          <h4 className="announcement-resources__heading">{getMessage("announcements.linksHeading")}</h4>
          <ul className="announcement-resources__list">
            {visibleLinks.map((link, index) => (
              <li key={`${announcementId}-link-${index}`}>
                <AnnouncementResourceAction
                  kind="link"
                  href={link.url}
                  title={link.label}
                  subtitle={link.showUrl ? link.url : ""}
                  cta={getMessage("announcements.openLink")}
                  ariaLabel={getMessage("announcements.openLinkAria", { label: link.label })}
                />
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

export default AnnouncementInteractiveResources;

AnnouncementInteractiveResources.propTypes = {
  announcementId: PropTypes.string.isRequired,
  links: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    url: PropTypes.string,
  })),
  attachments: PropTypes.arrayOf(PropTypes.shape({
    filename: PropTypes.string,
    url: PropTypes.string,
    mimeType: PropTypes.string,
  })),
};

AnnouncementInteractiveResources.defaultProps = {
  links: undefined,
  attachments: undefined,
};
